export type OverlayId =
  | "christmas"
  | "new-year"
  | "halloween"
  | "valentines"
  | "st-patricks"
  | "pi-day"
  | "hanukkah"
  | "passover"
  | "rosh-hashanah"
  | "purim"
  | "yom-haatzmaut"
  | "easter";

export interface Overlay {
  id: OverlayId;
  name: string;
  isActive: (date: Date) => boolean;
}

const dayOfYear = (month: number, day: number) => ({ month, day });

const isBetween = (
  date: Date,
  start: { month: number; day: number },
  end: { month: number; day: number },
): boolean => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const currentValue = month * 100 + day;
  const startValue = start.month * 100 + start.day;
  const endValue = end.month * 100 + end.day;
  if (startValue <= endValue) {
    return currentValue >= startValue && currentValue <= endValue;
  }
  return currentValue >= startValue || currentValue <= endValue;
};

const isOn = (date: Date, target: { month: number; day: number }): boolean =>
  date.getMonth() + 1 === target.month && date.getDate() === target.day;

interface HebrewWindow {
  start: string;
  end: string;
}

// Hebrew calendar + Easter dates — hardcoded per Gregorian year so detection
// is dead simple. Extend the table when the year rolls over.
const HEBREW_DATES: Record<number, Record<string, HebrewWindow>> = {
  2026: {
    purim: { start: "2026-03-02", end: "2026-03-03" },
    passover: { start: "2026-04-01", end: "2026-04-09" },
    yomHaatzmaut: { start: "2026-04-21", end: "2026-04-22" },
    roshHashanah: { start: "2026-09-11", end: "2026-09-13" },
    hanukkah: { start: "2026-12-04", end: "2026-12-12" },
    easter: { start: "2026-04-03", end: "2026-04-06" },
  },
  2027: {
    purim: { start: "2027-03-22", end: "2027-03-23" },
    passover: { start: "2027-04-21", end: "2027-04-29" },
    yomHaatzmaut: { start: "2027-05-11", end: "2027-05-12" },
    roshHashanah: { start: "2027-10-01", end: "2027-10-03" },
    hanukkah: { start: "2027-12-24", end: "2028-01-01" },
    easter: { start: "2027-03-26", end: "2027-03-29" },
  },
  2028: {
    purim: { start: "2028-03-11", end: "2028-03-12" },
    passover: { start: "2028-04-10", end: "2028-04-18" },
    yomHaatzmaut: { start: "2028-04-30", end: "2028-05-01" },
    roshHashanah: { start: "2028-09-20", end: "2028-09-22" },
    hanukkah: { start: "2028-12-12", end: "2028-12-20" },
    easter: { start: "2028-04-14", end: "2028-04-17" },
  },
  2029: {
    purim: { start: "2029-02-28", end: "2029-03-01" },
    passover: { start: "2029-03-30", end: "2029-04-07" },
    yomHaatzmaut: { start: "2029-04-18", end: "2029-04-19" },
    roshHashanah: { start: "2029-09-09", end: "2029-09-11" },
    hanukkah: { start: "2029-12-01", end: "2029-12-09" },
    easter: { start: "2029-03-30", end: "2029-04-02" },
  },
  2030: {
    purim: { start: "2030-03-18", end: "2030-03-19" },
    passover: { start: "2030-04-17", end: "2030-04-25" },
    yomHaatzmaut: { start: "2030-05-07", end: "2030-05-08" },
    roshHashanah: { start: "2030-09-27", end: "2030-09-29" },
    hanukkah: { start: "2030-12-20", end: "2030-12-28" },
    easter: { start: "2030-04-19", end: "2030-04-22" },
  },
  2031: {
    purim: { start: "2031-03-08", end: "2031-03-09" },
    passover: { start: "2031-04-07", end: "2031-04-15" },
    yomHaatzmaut: { start: "2031-04-27", end: "2031-04-28" },
    roshHashanah: { start: "2031-09-17", end: "2031-09-19" },
    hanukkah: { start: "2031-12-09", end: "2031-12-17" },
    easter: { start: "2031-04-11", end: "2031-04-14" },
  },
  2032: {
    purim: { start: "2032-02-25", end: "2032-02-26" },
    passover: { start: "2032-03-26", end: "2032-04-03" },
    yomHaatzmaut: { start: "2032-04-14", end: "2032-04-15" },
    roshHashanah: { start: "2032-09-05", end: "2032-09-07" },
    hanukkah: { start: "2032-11-27", end: "2032-12-05" },
    easter: { start: "2032-03-26", end: "2032-03-29" },
  },
  2033: {
    purim: { start: "2033-03-14", end: "2033-03-15" },
    passover: { start: "2033-04-13", end: "2033-04-21" },
    yomHaatzmaut: { start: "2033-05-04", end: "2033-05-05" },
    roshHashanah: { start: "2033-09-23", end: "2033-09-25" },
    hanukkah: { start: "2033-12-16", end: "2033-12-24" },
    easter: { start: "2033-04-15", end: "2033-04-18" },
  },
  2034: {
    purim: { start: "2034-03-04", end: "2034-03-05" },
    passover: { start: "2034-04-03", end: "2034-04-11" },
    yomHaatzmaut: { start: "2034-04-23", end: "2034-04-24" },
    roshHashanah: { start: "2034-09-13", end: "2034-09-15" },
    hanukkah: { start: "2034-12-06", end: "2034-12-14" },
    easter: { start: "2034-04-07", end: "2034-04-10" },
  },
  2035: {
    purim: { start: "2035-03-24", end: "2035-03-25" },
    passover: { start: "2035-04-23", end: "2035-05-01" },
    yomHaatzmaut: { start: "2035-05-13", end: "2035-05-14" },
    roshHashanah: { start: "2035-10-03", end: "2035-10-05" },
    hanukkah: { start: "2035-12-25", end: "2036-01-02" },
    easter: { start: "2035-03-23", end: "2035-03-26" },
  },
};

const isInHebrewWindow = (date: Date, key: keyof (typeof HEBREW_DATES)[2026]): boolean => {
  const year = date.getFullYear();
  const isoToday = date.toISOString().slice(0, 10);
  const lookupYears = [year, year - 1];
  for (const lookupYear of lookupYears) {
    const yearWindows = HEBREW_DATES[lookupYear];
    if (!yearWindows) continue;
    const window = yearWindows[key];
    if (!window) continue;
    if (isoToday >= window.start && isoToday <= window.end) return true;
  }
  return false;
};

export const OVERLAYS: Overlay[] = [
  {
    id: "new-year",
    name: "New Year",
    isActive: (date) => isBetween(date, dayOfYear(12, 31), dayOfYear(1, 2)),
  },
  {
    id: "valentines",
    name: "Valentine's Day",
    isActive: (date) => isOn(date, dayOfYear(2, 14)),
  },
  {
    id: "pi-day",
    name: "Pi Day",
    isActive: (date) => isOn(date, dayOfYear(3, 14)),
  },
  {
    id: "st-patricks",
    name: "St. Patrick's Day",
    isActive: (date) => isOn(date, dayOfYear(3, 17)),
  },
  {
    id: "purim",
    name: "Purim",
    isActive: (date) => isInHebrewWindow(date, "purim"),
  },
  {
    id: "passover",
    name: "Passover",
    isActive: (date) => isInHebrewWindow(date, "passover"),
  },
  {
    id: "easter",
    name: "Easter",
    isActive: (date) => isInHebrewWindow(date, "easter"),
  },
  {
    id: "yom-haatzmaut",
    name: "Yom Ha'atzmaut",
    isActive: (date) => isInHebrewWindow(date, "yomHaatzmaut"),
  },
  {
    id: "rosh-hashanah",
    name: "Rosh Hashanah",
    isActive: (date) => isInHebrewWindow(date, "roshHashanah"),
  },
  {
    id: "halloween",
    name: "Halloween",
    isActive: (date) => isBetween(date, dayOfYear(10, 29), dayOfYear(10, 31)),
  },
  {
    id: "hanukkah",
    name: "Hanukkah",
    isActive: (date) => isInHebrewWindow(date, "hanukkah"),
  },
  {
    id: "christmas",
    name: "Christmas",
    isActive: (date) => isBetween(date, dayOfYear(12, 24), dayOfYear(12, 26)),
  },
];

export const getActiveOverlay = (date: Date = new Date()): Overlay | null => {
  for (const overlay of OVERLAYS) {
    if (overlay.isActive(date)) return overlay;
  }
  return null;
};
